import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import Logo from "../components/Logo";
import ROIEstimator from "../components/ROIEstimator";
import PricingDemoClip from "../components/PricingDemoClip";
import { trackStep } from "../lib/analytics";
import { PromoBanner } from "../components/PromoBanner";
import { useAuth } from "../context/AuthContext";
import {
  Check, Sparkle, ArrowRight, X, UserFocus, Truck, Buildings,
  ChatCircleDots, ReceiptX, Wrench, Package, Headset, ChartLineUp,
  CurrencyDollar, ShieldCheck
} from "@phosphor-icons/react";

/*
  PRICING TIERS — Edit these numbers/features here as your pricing evolves.
  Two sections: Owner-Operators (1-3 trucks) and Fleets (4+ trucks).
*/
const OWNER_OP_TIERS = [
  {
    id: "solo",
    name: "Solo",
    tagline: "For the 1-truck owner-operator",
    price: 79,
    priceSuffix: "/mo",
    trucks: "1 truck",
    smsIncluded: "100 SMS/mo",
    features: [
      "1 driver profile (you)",
      "Unlimited AI-drafted messages",
      "Dispatch + invoicing tools",
      "Driver Portal (mobile-first)",
      "Public shipment tracking links",
      "AI Profit Calculator per load",
      "Email support (48hr)",
    ],
    cta: "Start Solo",
    highlight: false,
  },
  {
    id: "duo",
    name: "Duo",
    tagline: "For 2-truck teams",
    price: 129,
    priceSuffix: "/mo",
    trucks: "2 trucks",
    smsIncluded: "250 SMS/mo",
    features: [
      "2 driver profiles",
      "Everything in Solo",
      "Maintenance reminders",
      "AI driver assignment scoring",
      "Priority AI drafting queue",
      "Email support (24hr)",
    ],
    cta: "Start Duo",
    highlight: true,
    badge: "MOST POPULAR FOR OWNER-OPS",
  },
  {
    id: "trio",
    name: "Trio",
    tagline: "For 3-truck fleets",
    price: 179,
    priceSuffix: "/mo",
    trucks: "3 trucks",
    smsIncluded: "500 SMS/mo",
    features: [
      "3 driver profiles",
      "Everything in Duo",
      "Recruiting pipeline",
      "Lead capture (contact form)",
      "PDF invoice generation",
      "Live chat support",
    ],
    cta: "Start Trio",
    highlight: false,
  },
];

const FLEET_TIERS = [
  {
    id: "small_fleet",
    name: "Small Fleet",
    tagline: "For growing fleets",
    price: 299,
    priceSuffix: "/mo",
    trucks: "4-10 trucks",
    smsIncluded: "1,500 SMS/mo",
    features: [
      "Up to 10 driver profiles",
      "Everything in Trio",
      "Bulk load creation",
      "Custom AI message templates",
      "Priority AI + support (12hr)",
      "Onboarding call included",
    ],
    cta: "Start Small Fleet",
    highlight: false,
  },
  {
    id: "growth",
    name: "Growth",
    tagline: "For established carriers",
    price: 499,
    priceSuffix: "/mo",
    trucks: "11-25 trucks",
    smsIncluded: "4,000 SMS/mo",
    features: [
      "Up to 25 driver profiles",
      "Everything in Small Fleet",
      "Multi-dispatcher accounts",
      "Advanced analytics dashboard",
      "Priority AI + phone support",
      "White-glove onboarding",
    ],
    cta: "Start Growth",
    highlight: true,
    badge: "MOST POPULAR FOR FLEETS",
  },
];

const ENTERPRISE_TIERS = [
  {
    id: "fleet_50",
    name: "Fleet 50",
    tagline: "For 26-50 truck operations",
    price: 999,
    priceSuffix: "/mo",
    trucks: "up to 50 trucks",
    smsIncluded: "10,000 SMS/mo",
    features: [
      "Everything in Growth",
      "Multi-user + role permissions",
      "DAT + Truckstop load boards",
      "QuickBooks / NetSuite sync",
      "Factoring integrations",
      "Priority support (8hr)",
    ],
    cta: "Book a Demo",
    highlight: false,
  },
  {
    id: "fleet_100",
    name: "Fleet 100",
    tagline: "For 51-100 truck fleets",
    price: 1999,
    priceSuffix: "/mo",
    trucks: "up to 100 trucks",
    smsIncluded: "25,000 SMS/mo",
    features: [
      "Everything in Fleet 50",
      "SSO (Google + Microsoft Entra)",
      "ELD integration (beta)",
      "Custom API access",
      "Dedicated account manager",
      "Response SLA (24hr)",
    ],
    cta: "Book a Demo",
    highlight: true,
    badge: "MOST POPULAR FOR ENTERPRISE",
  },
  {
    id: "enterprise",
    name: "Enterprise",
    tagline: "For fleets of 100+ trucks",
    price: null,
    priceSuffix: "Custom",
    trucks: "Unlimited",
    smsIncluded: "Custom SMS",
    features: [
      "Everything in Fleet 100",
      "Full EDI 204/210/214",
      "Custom SLA + data retention",
      "Named success partner",
      "Monthly business reviews",
      "Custom integrations on request",
    ],
    cta: "Talk to Sales",
    highlight: false,
  },
];

const COMPARE_ROWS = [
  { icon: ChatCircleDots, label: "AI-drafted messages", solo: "Unlimited", duo: "Unlimited", trio: "Unlimited", small: "Unlimited", growth: "Unlimited", ent: "Unlimited" },
  { icon: Truck, label: "Driver profiles", solo: "1", duo: "2", trio: "3", small: "10", growth: "25", ent: "Unlimited" },
  { icon: Package, label: "Loads / month", solo: "50", duo: "125", trio: "250", small: "1,000", growth: "3,000", ent: "Unlimited" },
  { icon: ReceiptX, label: "PDF invoices", solo: true, duo: true, trio: true, small: true, growth: true, ent: true },
  { icon: Wrench, label: "Maintenance reminders", solo: false, duo: true, trio: true, small: true, growth: true, ent: true },
  { icon: UserFocus, label: "Recruiting pipeline", solo: false, duo: false, trio: true, small: true, growth: true, ent: true },
  { icon: ChartLineUp, label: "Analytics dashboard", solo: false, duo: false, trio: false, small: false, growth: true, ent: true },
  { icon: Headset, label: "Multi-dispatcher accounts", solo: false, duo: false, trio: false, small: false, growth: true, ent: true },
  { icon: Buildings, label: "Load board integrations", solo: false, duo: false, trio: false, small: false, growth: false, ent: true },
  { icon: CurrencyDollar, label: "Factoring integrations", solo: false, duo: false, trio: false, small: false, growth: false, ent: true },
  { icon: ShieldCheck, label: "Support", solo: "Email 48hr", duo: "Email 24hr", trio: "Live chat", small: "Priority 12hr", growth: "Phone + priority", ent: "Dedicated manager" },
];

const FAQS = [
  { q: "Do you charge per driver or per truck?", a: "Per truck. If you have 3 trucks with 3 dedicated drivers, that's the Trio plan ($179/mo). If a driver leaves and you replace them, no extra charge — you're paying for the truck slot, not the individual." },
  { q: "What if I go over my monthly SMS limit?", a: "SMS overages are charged at $0.02 per message (~2.5× Twilio's raw cost, which covers infrastructure). You'll get an in-app alert at 80% of your quota so there are no surprises." },
  { q: "Can I switch tiers anytime?", a: "Yes — upgrade instantly (billed prorated), or downgrade at the next billing cycle. No annual contracts, no cancellation fees." },
  { q: "Is there a free trial?", a: "Yes — 7 days free on any tier with full access. A card is required to start so your account is ready the day the trial ends; nothing is charged until day 8 and you can cancel in one click before then." },
  { q: "What happens to my data if I cancel?", a: "You can export all your loads, drivers, invoices, and contacts as CSV/PDF at any time. After cancellation, your data is retained for 30 days in case you come back, then permanently deleted." },
  { q: "Do you offer discounts for annual billing?", a: "Yes — save 2 months when you pay annually (effectively 16.7% off). Contact us if you want the annual price for your tier." },
];

export default function PricingPage() {
  useEffect(() => { trackStep("pricing"); }, []);
  return (
    <div className="min-h-screen relative noise-bg" data-testid="pricing-page">
      {/* Header */}
      <header className="sticky top-0 z-30 backdrop-blur-md bg-[#0A0A0A]/85 border-b border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 py-4 flex items-center justify-between">
          <Link to="/" data-testid="pricing-nav-home">
            <Logo variant="sm" />
          </Link>
          <nav className="hidden md:flex items-center gap-8 text-sm">
            <Link to="/services" className="text-neutral-400 hover:text-white transition-colors">Services</Link>
            <Link to="/pricing" className="text-white font-medium">Pricing</Link>
            <Link to="/contact" className="text-neutral-400 hover:text-white transition-colors">Contact</Link>
            <Link to="/login" className="text-neutral-400 hover:text-white transition-colors">Sign in</Link>
          </nav>
          <Link
            to="/signup"
            className="btn-primary rounded-full px-5 py-2 text-sm inline-flex items-center gap-2"
            data-testid="pricing-nav-signup"
          >
            Start free trial <ArrowRight size={13} weight="bold" />
          </Link>
        </div>
      </header>

      {/* Hero */}
      <section className="relative z-10 max-w-7xl mx-auto px-4 sm:px-8 pt-16 sm:pt-24 pb-8 text-center">
        <div className="inline-flex items-center gap-2 pill pill-primary mb-6" data-testid="pricing-hero-pill">
          <Sparkle size={12} weight="fill" /> 7-DAY FREE TRIAL · CANCEL ANYTIME
        </div>
        <h1 className="font-display text-4xl sm:text-5xl md:text-6xl font-semibold tracking-tight leading-[1.05] max-w-4xl mx-auto">
          Pricing that scales with your <span className="text-[#FF5A1F]">fleet</span>.
        </h1>
        <p className="mt-6 text-neutral-400 text-base sm:text-lg max-w-2xl mx-auto leading-relaxed">
          Whether you drive one truck yourself or run a 50-truck operation, there's a plan that fits.
          Every tier includes unlimited AI drafts and no long-term contracts.
        </p>
      </section>

      {/* Ad visitors see the product (real screens) before the price */}
      <PricingDemoClip />

      <PromoBanner />
      <PricingFuaSlot />

      {/* ROI Estimator — drives conversions before showing the plan grid */}
      <ROIEstimator />

      {/* Owner-Op section */}
      <section id="pricing-tiers" className="relative z-10 max-w-7xl mx-auto px-4 sm:px-8 py-10">
        <div className="mb-8 sm:mb-10 text-center">
          <div className="overline mb-3">FOR OWNER-OPERATORS</div>
          <h2 className="font-display text-2xl sm:text-3xl md:text-4xl font-semibold tracking-tight">
            Running 1–3 trucks?
          </h2>
          <p className="text-neutral-400 mt-3 text-sm sm:text-base max-w-xl mx-auto">
            Built for the folks who ARE the dispatcher, the driver, and the accountant. Save your evenings.
          </p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-5" data-testid="owner-op-tiers">
          {OWNER_OP_TIERS.map((t) => (
            <TierCard key={t.id} tier={t} testid={`tier-${t.id}`} />
          ))}
        </div>
      </section>

      {/* Fleet section */}
      <section className="relative z-10 max-w-7xl mx-auto px-4 sm:px-8 py-10 sm:py-16">
        <div className="mb-8 sm:mb-10 text-center">
          <div className="overline mb-3">FOR FLEETS</div>
          <h2 className="font-display text-2xl sm:text-3xl md:text-4xl font-semibold tracking-tight">
            Running 4-25 trucks?
          </h2>
          <p className="text-neutral-400 mt-3 text-sm sm:text-base max-w-xl mx-auto">
            Multi-dispatcher accounts, analytics, and priority AI for carriers on the way up.
          </p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-5 max-w-4xl mx-auto" data-testid="fleet-tiers">
          {FLEET_TIERS.map((t) => (
            <TierCard key={t.id} tier={t} testid={`tier-${t.id}`} />
          ))}
        </div>
      </section>

      {/* Enterprise Fleets section */}
      <section id="enterprise-tiers" className="relative z-10 max-w-7xl mx-auto px-4 sm:px-8 py-10 sm:py-16 border-t border-white/10">
        <div className="mb-8 sm:mb-10 text-center">
          <div className="overline mb-3">FOR ENTERPRISE FLEETS</div>
          <h2 className="font-display text-2xl sm:text-3xl md:text-4xl font-semibold tracking-tight">
            Running 50+ trucks?
          </h2>
          <p className="text-neutral-400 mt-3 text-sm sm:text-base max-w-2xl mx-auto">
            Multi-user roles, SSO, ELD integration, EDI, and a dedicated success partner.
            <Link to="/enterprise" className="text-[#FF5A1F] hover:text-[#FF7038] transition-colors ml-1 inline-flex items-center gap-1">
              See the full enterprise pitch <ArrowRight size={12} weight="bold" />
            </Link>
          </p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-5" data-testid="enterprise-tiers">
          {ENTERPRISE_TIERS.map((t) => (
            <TierCard key={t.id} tier={t} testid={`tier-${t.id}`} />
          ))}
        </div>
      </section>

      {/* Feature comparison table */}
      <section className="relative z-10 max-w-7xl mx-auto px-4 sm:px-8 py-16 sm:py-24 border-t border-white/10" data-testid="pricing-compare">
        <div className="mb-8 sm:mb-10 text-center">
          <div className="overline mb-3">COMPARE ALL PLANS</div>
          <h2 className="font-display text-2xl sm:text-3xl md:text-4xl font-semibold tracking-tight">
            What's in each tier?
          </h2>
        </div>

        <div className="overflow-x-auto -mx-4 sm:mx-0">
          <table className="min-w-[720px] w-full text-sm">
            <thead>
              <tr className="border-b border-white/10">
                <th className="text-left font-normal text-xs uppercase tracking-widest text-neutral-500 py-4 px-3">Feature</th>
                <th className="font-semibold text-white py-4 px-3 text-center">Solo</th>
                <th className="font-semibold text-white py-4 px-3 text-center">Duo</th>
                <th className="font-semibold text-white py-4 px-3 text-center">Trio</th>
                <th className="font-semibold text-white py-4 px-3 text-center">Small Fleet</th>
                <th className="font-semibold text-white py-4 px-3 text-center">Growth</th>
                <th className="font-semibold text-white py-4 px-3 text-center">Enterprise</th>
              </tr>
            </thead>
            <tbody>
              {COMPARE_ROWS.map((row, i) => (
                <tr key={i} className="border-b border-white/5">
                  <td className="py-3.5 px-3 text-neutral-300">
                    <div className="flex items-center gap-2">
                      <row.icon size={14} className="text-neutral-500" />
                      {row.label}
                    </div>
                  </td>
                  {["solo", "duo", "trio", "small", "growth", "ent"].map((k) => (
                    <td key={k} className="py-3.5 px-3 text-center">
                      <CompareCell value={row[k]} />
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {/* FAQs */}
      <section className="relative z-10 max-w-4xl mx-auto px-4 sm:px-8 py-16 sm:py-24 border-t border-white/10" data-testid="pricing-faq">
        <div className="mb-8 sm:mb-10">
          <div className="overline mb-3">PRICING FAQ</div>
          <h2 className="font-display text-2xl sm:text-3xl md:text-4xl font-semibold tracking-tight">
            Common questions
          </h2>
        </div>
        <div className="space-y-3">
          {FAQS.map((f, i) => (
            <FAQItem key={i} q={f.q} a={f.a} idx={i} />
          ))}
        </div>
      </section>

      {/* Final CTA */}
      <section className="relative z-10 max-w-4xl mx-auto px-4 sm:px-8 py-16 sm:py-24 text-center border-t border-white/10">
        <h2 className="font-display text-3xl sm:text-4xl md:text-5xl font-semibold tracking-tight leading-[1.05]">
          Try FreightPilot AI™ for <span className="text-[#FF5A1F]">7 days free</span>.
        </h2>
        <p className="mt-5 text-neutral-400 max-w-xl mx-auto text-sm sm:text-base leading-relaxed">
          7-day free trial — card required, $0 today, cancel anytime before day 8. Import your first load and see AI draft your first dispatch message in under 60 seconds.
        </p>
        <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-3">
          <Link
            to="/signup"
            data-testid="pricing-final-signup"
            className="btn-primary rounded-full px-7 py-3.5 text-sm inline-flex items-center gap-2"
          >
            Start free trial <ArrowRight size={14} weight="bold" />
          </Link>
          <Link
            to="/contact"
            data-testid="pricing-final-demo"
            className="rounded-full px-7 py-3.5 text-sm inline-flex items-center gap-2 border border-white/15 hover:border-white/30 text-neutral-200 hover:text-white transition-colors"
          >
            Book a demo instead
          </Link>
        </div>
      </section>

      {/* Footer */}
      <footer className="relative z-10 border-t border-white/10 py-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 flex flex-col sm:flex-row items-center justify-between gap-4 text-xs text-neutral-500">
          <div>© {new Date().getFullYear()} Freight Pilot AI LLC · Sioux Falls, SD · FreightPilot AI™. All rights reserved.</div>
          <div className="flex gap-6">
            <Link to="/services" className="hover:text-white transition-colors">Services</Link>
            <Link to="/pricing" className="hover:text-white transition-colors">Pricing</Link>
            <Link to="/contact" className="hover:text-white transition-colors">Contact</Link>
            <Link to="/privacy" className="hover:text-white transition-colors">Privacy</Link>
            <Link to="/terms" className="hover:text-white transition-colors">Terms</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}

function TierCard({ tier, testid }) {
  const isEnt = tier.price === null;
  const isDemoOnly = tier.cta === "Book a Demo" || tier.cta === "Talk to Sales";
  const { user } = useAuth();
  const navigate = useNavigate();

  const handleCta = () => {
    if (isEnt || isDemoOnly) {
      // Enterprise / Fleet 50 / Fleet 100 / any custom-priced or sales-led tier
      navigate("/demo");
      return;
    }
    if (user) {
      // Logged in — go straight to billing with this plan pre-selected
      navigate(`/app/billing?plan=${tier.id}`);
    } else {
      // Not logged in — start free trial, remember chosen plan for post-signup
      try { sessionStorage.setItem("chosen_plan", tier.id); } catch {}
      trackStep("plan_click");
      navigate(`/signup?plan=${tier.id}`);
    }
  };

  return (
    <div
      className={`card-surface p-6 sm:p-7 flex flex-col relative overflow-hidden ${
        tier.highlight ? "border-[#FF5A1F]/40 ring-1 ring-[#FF5A1F]/20" : ""
      }`}
      data-testid={testid}
    >
      {tier.highlight && tier.badge && (
        <div className="absolute top-0 right-0 bg-[#FF5A1F] text-black text-[9px] font-bold uppercase tracking-widest px-3 py-1.5 rounded-bl-lg">
          {tier.badge}
        </div>
      )}
      <div className="mb-6">
        <div className="font-display text-xl sm:text-2xl font-semibold text-white">{tier.name}</div>
        <div className="text-xs text-neutral-500 mt-1">{tier.tagline}</div>
      </div>

      <div className="mb-6">
        {isEnt ? (
          <div>
            <div className="font-display text-4xl sm:text-5xl font-semibold text-white">Custom</div>
            <div className="text-xs text-neutral-500 mt-1">Contact us for pricing</div>
          </div>
        ) : (
          <div>
            <div className="flex items-baseline gap-1">
              <span className="text-neutral-500 text-lg">$</span>
              <span className="font-display text-4xl sm:text-5xl font-semibold text-white">{tier.price}</span>
              <span className="text-neutral-500 text-sm">{tier.priceSuffix}</span>
            </div>
            <div className="text-xs text-neutral-500 mt-1">or save 2 months with annual billing</div>
          </div>
        )}
      </div>

      <div className="pb-4 mb-4 border-b border-white/10 flex items-center justify-between text-xs">
        <div className="flex items-center gap-1.5 text-neutral-400">
          <Truck size={12} className="text-[#FF5A1F]" /> {tier.trucks}
        </div>
        <div className="flex items-center gap-1.5 text-neutral-400">
          <ChatCircleDots size={12} className="text-[#FF5A1F]" /> {tier.smsIncluded}
        </div>
      </div>

      <ul className="space-y-2.5 mb-7 flex-1">
        {tier.features.map((f, i) => (
          <li key={i} className="flex items-start gap-2 text-sm text-neutral-300">
            <Check size={14} weight="bold" className="text-[#FF5A1F] mt-0.5 shrink-0" />
            <span>{f}</span>
          </li>
        ))}
      </ul>

      <button
        onClick={handleCta}
        data-testid={`${testid}-cta`}
        className={`w-full text-center rounded-full px-5 py-3 text-sm font-semibold inline-flex items-center justify-center gap-2 transition-colors ${
          tier.highlight
            ? "bg-[#FF5A1F] text-black hover:bg-[#FF7038]"
            : "border border-white/15 text-white hover:border-white/30"
        }`}
      >
        {tier.cta} <ArrowRight size={13} weight="bold" />
      </button>
    </div>
  );
}

function CompareCell({ value }) {
  if (value === true) return <Check size={16} weight="bold" className="text-[#FF5A1F] mx-auto" />;
  if (value === false) return <X size={14} className="text-neutral-700 mx-auto" />;
  return <span className="text-xs text-neutral-300">{value}</span>;
}

function FAQItem({ q, a, idx }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="card-surface overflow-hidden" data-testid={`pricing-faq-${idx}`}>
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between gap-4 p-5 text-left hover:bg-white/[0.02] transition-colors"
      >
        <span className="font-medium text-white text-sm sm:text-base">{q}</span>
        <span className={`text-[#FF5A1F] transition-transform shrink-0 ${open ? "rotate-45" : ""}`}>+</span>
      </button>
      {open && (
        <div className="px-5 pb-5 -mt-1 text-neutral-400 text-sm leading-relaxed border-t border-white/5 pt-4">
          {a}
        </div>
      )}
    </div>
  );
}


function PricingFuaSlot() {
  const { user } = useAuth();
  return null;
}
